import { useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./Icon.js";

/**
 * Portato da `components/lists/ListRow.jsx` del design system.
 * La riga generica tappabile: impostazioni, membri, categorie, selettori.
 *
 * L'originale inietta il chevron come HTML grezzo; qui usa il componente Icon,
 * così esiste una sola sorgente per le icone.
 */
export interface ListRowProps {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  value?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}

export function ListRow({
  leading,
  title,
  subtitle,
  value,
  meta,
  trailing,
  chevron,
  selected = false,
  danger = false,
  disabled = false,
  onClick,
  style,
}: ListRowProps) {
  const [hover, setHover] = useState(false);
  const tappable = Boolean(onClick) && !disabled;
  const showChevron = chevron === undefined ? tappable && !trailing : chevron;

  return (
    <div
      role={tappable ? "button" : undefined}
      tabIndex={tappable ? 0 : undefined}
      onClick={tappable ? onClick : undefined}
      onKeyDown={(event) => {
        if (tappable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick?.();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 56,
        padding: "10px 4px",
        cursor: tappable ? "pointer" : "default",
        opacity: disabled ? 0.4 : 1,
        color: danger ? "var(--coral-500)" : "var(--text-primary)",
        background: tappable && hover ? "var(--surface-sunken)" : "transparent",
        borderRadius: "var(--radius-sm)",
        paddingLeft: tappable ? 8 : 4,
        paddingRight: 8,
        transition: "background var(--duration-fast) var(--ease-standard)",
        ...style,
      }}
    >
      {leading && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            color: danger ? "var(--coral-500)" : "var(--ink-700)",
          }}
        >
          {leading}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-text)",
            fontSize: 13.5,
            fontWeight: 600,
            letterSpacing: "-0.1px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontFamily: "var(--font-text)",
              fontSize: 11.5,
              fontWeight: 500,
              color: "var(--text-secondary)",
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subtitle}
          </div>
        )}
      </div>

      {(value || meta) && (
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          {value && (
            <div
              style={{
                fontFamily: "var(--font-numeric)",
                fontSize: 11.5,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.02em",
              }}
            >
              {value}
            </div>
          )}
          {meta && (
            <div
              style={{
                fontFamily: "var(--font-text)",
                fontSize: 11,
                fontWeight: 500,
                color: "var(--text-tertiary)",
                marginTop: 2,
              }}
            >
              {meta}
            </div>
          )}
        </div>
      )}

      {trailing && (
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{trailing}</div>
      )}

      {selected && (
        <span
          style={{
            flexShrink: 0,
            width: 8,
            height: 8,
            borderRadius: 999,
            background: "var(--lime-500)",
          }}
        />
      )}

      {showChevron && (
        <span style={{ flexShrink: 0, color: "var(--ink-300)", display: "flex" }}>
          <Icon name="chevron-right" size={16} strokeWidth={2.2} />
        </span>
      )}
    </div>
  );
}
